/* STUB IMPLEMENTATION — see src/stubs/README.md.
 *
 * The one thing the two stub clients have to agree on: what a tenancy chose in
 * the account decides who its cluster console lets in. In a deployment that is
 * a database on the other side of two different origins; here it is one blob in
 * `sessionStorage`, so the choice survives the reload that moving between the
 * two entry points causes.
 *
 * The change of provider is modelled as the server models it: one state means
 * "under way", the cluster never moves off what it runs when an attempt is
 * refused, and going back aims at what ran before.
 *
 * The cluster session *is* in here, unlike the account's, and for a reason that
 * is the flow rather than convenience: signing in to a cluster console leaves
 * for another site and comes back, so a session that did not survive a page
 * load could not exist at all. An account session still does not survive one —
 * a reload signs you out there, as before.
 */

import type {
  CertificateReport,
  ClusterActor,
  ClusterSession,
  ComponentVersion,
  IdentityChoice,
  ReconcileReport,
  ReconcileRun,
  Resource,
  ResourceInput,
  Scope,
  Rollout,
  RolloutBoard,
  RolloutWave,
  SettingsState,
  TenancyIdentity,
  Tenant,
  TenantMove,
  TenantUsage,
  TenantVersions,
  TrailEntry,
  UsageGap,
  UsagePeriod,
} from '../api/types';
import { RESOURCE_KINDS } from '../api/types';
import { ApiError } from '../api/errors';
import type { StubTrailEntry } from './fixtures';
import {
  STUB_ACCOUNTS,
  STUB_AGENTS,
  STUB_CONNECTOR_UNTOLD,
  STUB_CLIENT_UNTOLD,
  STUB_CLUSTER_ACTOR,
  STUB_EXPORT_UNREADABLE,
  STUB_IDENTITY,
  STUB_MOVE_FAILS,
  STUB_MOVE_FAILURE,
  STUB_NOT_READ,
  STUB_OBSERVED_AT,
  STUB_OTHER_IDENTITIES,
  STUB_PEOPLE_USAGE,
  STUB_PROVIDER_ISSUER,
  STUB_REPORTS,
  STUB_RESOURCES,
  STUB_SCOPES,
  STUB_RESOURCE_USAGE,
  STUB_SESSIONS_USAGE,
  STUB_TRAIL,
  STUB_STANDINGS,
  STUB_TENANCY,
  STUB_TENANTS,
  STUB_UNNAMEABLE_SUBJECT,
  STUB_VERSIONS,
  STUB_WAVES,
  stubBudgetIn,
  stubFirstCertificate,
  stubRead,
  stubReportSteps,
  stubSettingsIn,
  stubUnread,
} from './fixtures';
import { forecast, judgePairs } from './stubRollout';

/**
 * Versioned, and that is the whole point of the suffix: the shape and the
 * starting point of this state change as the demonstration grows, and a tab
 * that has run an older one keeps it in `sessionStorage` until somebody thinks
 * to clear it by hand. Nobody thinks to. Bumping the number retires the old
 * state instead of asking.
 */
const KEY = 'gdsgate.stub.state.v2';

/** How long a change takes in the demonstration. */
const APPLY_MS = 6000;

/** An issuer under this host is refused by the check: nothing is touched. */
const REJECTING_HOST = 'https://broken.';

/** An issuer under this host passes the check and then does not come back up. */
const FAILING_HOST = 'https://unstable.';

/**
 * An issuer under this host answers, and its certificate does not verify.
 *
 * Its own host beside the refusing one because the two are different things to
 * a person: this provider is up and answering everybody else, and what has to
 * be dealt with is the certificate rather than the network.
 */
export const EXPIRED_HOST = 'https://expired.';

/** Whether an issuer is one this stand refuses, and with which code. */
export function stubRefusal(issuer: string): 'issuer_certificate' | 'issuer_unreachable' | null {
  if (issuer.startsWith(EXPIRED_HOST)) return 'issuer_certificate';
  if (issuer.startsWith(REJECTING_HOST)) return 'issuer_unreachable';
  return null;
}

/**
 * What the pretend identity provider does with the next sign-in.
 *
 * Four answers, because four are the ones worth seeing: a person the cluster
 * admits, a person it does not, an identity that fails verification, and a
 * provider that says nothing at all. The fifth and sixth refusals need no
 * switch — a cluster with no provider applied answers `sign_in_unavailable` on
 * its own, and a return nobody started answers `sign_in_expired`.
 */
export type StubVouch = 'admin' | 'stranger' | 'refused' | 'unreachable';

interface StubState {
  identity: TenancyIdentity;
  tenants: Tenant[];
  /**
   * The last application of each tenancy, by slug — the report its card shows.
   *
   * Kept beside the tenancies rather than on them, as the server keeps it: a
   * report belongs to the pass that produced it, and a tenancy declared here has
   * none until something is applied to it.
   */
  reports: Record<string, ReconcileReport>;
  /**
   * Where each tenancy stands on what has been rendered for its roles, by slug.
   *
   * Kept here and not read back out of the last report, because it is not the
   * report's to say: whether a role has read a value depends on whether its
   * process has started since the value was written, which is a fact about the
   * tenancy that outlives every pass over it. This is the stub's stand-in for the
   * witness the control plane keeps beside a tenancy, and it is why applying
   * brightpath twice does not turn a number it has not read into one it has.
   */
  standings: Record<string, SettingsState>;
  resources: Resource[];
  /** The boundaries the stub tenancy declares, in prefix order. */
  scopes: Scope[];
  /** Whether this pretend deployment runs an identity provider of its own. */
  providerAvailable: boolean;
  vouch: StubVouch;
  /** The `state` of the sign-in this browser started, if it started one. */
  signInState: string | null;
  clusterSignedIn: boolean;
  /** Whether the pretend cluster answers when a write is carried to it. */
  clusterReachable: boolean;
  /** Whether the pretend provider names this person in a form gdsgate records. */
  nameable: boolean;
  /**
   * Whether the tenancy's exported audit chain can be read.
   *
   * `false` by default, because that is the deployment as it stands: the export
   * lives inside the tenancy's own Auth container and the control plane cannot
   * reach it, so sessions and people arrive missing with a reason. The switch
   * exists so the counted case can be seen beside it — the two must not look
   * alike on screen.
   */
  auditExport: boolean;
  /**
   * Our own trail, newest first — one list for every tenancy, filtered on the
   * way out as the server filters it in the query.
   *
   * Written to by whatever is done in this tab, because that is what makes the
   * screen worth having: the line somebody just caused is the one they look
   * for. The vocabulary is copied from the server rather than invented.
   */
  trail: StubTrailEntry[];
  /** Which wave each tenancy moves in, by slug. Absent means the rest wave. */
  waves: Record<string, string>;
  /** What each tenancy's components were last seen running, by slug. */
  versions: Record<string, ComponentVersion[]>;
  /** When those readings were taken. Absent means they never were. */
  observedAt: Record<string, string>;
  /** The rollout under way, if one is. At most one, as on the server. */
  rollout: StubRollout | null;
  /** What that rollout has done, tenancy by tenancy. */
  steps: StubStep[];
  /**
   * Whether the wave under way counts as having stood at its new version.
   *
   * In a deployment this is half an hour of wall clock. Here it is a switch,
   * because the refusal it produces — and the fact that the refusal is the
   * machine's rather than a judgement — is worth being able to see without
   * waiting for it.
   */
  soaked: boolean;
  /**
   * Whether the tenancy whose move fails has been put right.
   *
   * The other half of trying again, and it has to be a switch: without it the
   * demonstration could show a wave held up by a failure and never show the way
   * out of one. Turning it on is what an operator does away from this console —
   * they go and fix the cluster — and the move that follows then works, leaving
   * a step that reads `moved` with a failure behind it.
   */
  repaired: boolean;
}

/** The rollout, in the shape the board answers with plus what it is doing. */
interface StubRollout {
  target: string;
  state: string;
  wave: string;
  detail: string;
  actor: string;
  started_at: string;
  changed_at: string;
  /** When it ended, if it has. `null` while it is going. */
  finished_at: string | null;
  /** When the wave under way began moving, so the move can finish by the clock. */
  movingSince: string | null;
  /**
   * Whose move the walk in progress is for: the whole wave (`null`), or the
   * tenancies a second attempt was asked for.
   *
   * The same distinction the server draws, and the only one: everything after
   * it — one at a time, in slug order, leaving the wave waiting at the end — is
   * the same walk either way.
   */
  moving: string[] | null;
}

interface StubStep {
  slug: string;
  wave: string;
  outcome: string;
  /** What it was on before this rollout touched it. A second attempt leaves it. */
  from: string;
  detail: string;
  /** How many times this rollout moved it, and how many of those did not work. */
  attempts: number;
  failures: number;
  changed_at: string;
}

function fresh(): StubState {
  return {
    identity: { ...STUB_IDENTITY },
    tenants: [...STUB_TENANTS],
    reports: { ...STUB_REPORTS },
    standings: { ...STUB_STANDINGS },
    resources: [...STUB_RESOURCES],
    scopes: [...STUB_SCOPES],
    providerAvailable: true,
    vouch: 'admin',
    signInState: null,
    clusterSignedIn: false,
    clusterReachable: true,
    nameable: true,
    auditExport: false,
    trail: [...STUB_TRAIL],
    waves: { ...STUB_WAVES },
    versions: { ...STUB_VERSIONS },
    observedAt: { ...STUB_OBSERVED_AT },
    rollout: null,
    steps: [],
    soaked: false,
    repaired: false,
  };
}

let state: StubState | null = null;

function load(): StubState {
  if (state !== null) return state;

  const raw = sessionStorage.getItem(KEY);
  if (raw !== null) {
    try {
      // Merged over the fixtures rather than trusted whole: a blob written by
      // an older build of this demonstration is missing whatever was added
      // since, and a missing switch would read as `undefined` rather than as
      // its default.
      state = { ...fresh(), ...(JSON.parse(raw) as Partial<StubState>) };
      return state;
    } catch {
      // A fixture that will not parse is a fixture, not a crisis.
    }
  }
  state = fresh();
  return state;
}

function save(next: StubState): StubState {
  state = next;
  sessionStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

/** Back to the fixtures. Called on sign-out, so the next run starts clean. */
export function resetStubState(): void {
  save(fresh());
}

/**
 * Writes one line of our own trail, in front of the ones already there.
 *
 * The sequence counts up from the highest there is, as a monotonic sequence
 * does; it is not the length of the list, because the list is a window on the
 * end of a record whose beginning was cut off long ago.
 */
function note(
  current: StubState,
  entry: { slug: string; actor: string; action: string; outcome: string; detail: string },
): StubState {
  const highest = current.trail.reduce((top, line) => Math.max(top, line.seq), 0);
  return {
    ...current,
    trail: [{ ...entry, seq: highest + 1, at: new Date().toISOString() }, ...current.trail],
  };
}

/**
 * One tenancy's trail: the last hundred lines, newest first.
 *
 * The same window the server answers with, and cut here for the same reason it
 * is cut there — there is no cursor, and a console that showed a thousand lines
 * of somebody else's storage would be inventing a page nobody can ask for.
 */
export function stubTrail(slug: string): TrailEntry[] {
  return load()
    .trail.filter((line) => line.slug === slug)
    .slice(0, 100)
    .map(({ slug: _slug, ...entry }) => entry);
}

/**
 * A change is a wall-clock affair rather than a timer: whoever reads the state
 * next is the one who notices it finished. That way it also finishes across the
 * reload between the two entry points.
 */
function settle(current: StubState): StubState {
  const identity = current.identity;
  if (!identity.in_flight) return current;

  const startedAt = identity.started_at;
  if (startedAt !== undefined && Date.now() - new Date(startedAt).getTime() < APPLY_MS) {
    return current;
  }

  const finished = new Date().toISOString();
  const target = identity.desired;
  const refused = target.kind === 'own' ? stubRefusal(target.issuer) : null;
  const rejected = refused !== null;
  const failed = target.kind === 'own' && target.issuer.startsWith(FAILING_HOST);

  if (rejected || failed) {
    return save({
      ...current,
      identity: {
        ...identity,
        state: rejected ? 'rejected' : 'failed',
        in_flight: false,
        failure: refused ?? 'cluster_unreachable',
        detail:
          refused === 'issuer_certificate'
            ? `issuer certificate: ${target.issuer} presented a certificate that expired on 2024-07-02T23:59:59Z`
            : refused === 'issuer_unreachable'
              ? 'discovery: the issuer did not answer'
              : 'restart: the cluster did not come back on the new provider, and was put back',
        finished_at: finished,
        changed_at: finished,
      },
    });
  }

  const previous = identity.applied;
  const settled: TenancyIdentity = {
    ...identity,
    state: 'applied',
    in_flight: false,
    applied: target,
    revertable: previous !== undefined,
    detail: `now on ${target.kind === 'provider' ? "the provider's identity provider" : target.issuer}`,
    finished_at: finished,
    changed_at: finished,
  };
  delete settled.failure;
  if (previous === undefined) delete settled.previous;
  else settled.previous = previous;

  return save({ ...current, identity: settled });
}

/** What the account's identity routes answer with, deployment settings folded in. */
export function readIdentity(): TenancyIdentity {
  const current = settle(load());
  const identity: TenancyIdentity = {
    ...current.identity,
    provider_available: current.providerAvailable,
  };
  if (current.providerAvailable) identity.provider_issuer = STUB_PROVIDER_ISSUER;
  else delete identity.provider_issuer;
  return identity;
}

/**
 * Starts a change, or refuses because one is already running.
 *
 * `action` is the word the server files it under — the three routes that start
 * one are told apart in the trail, and a stub that filed all three as one would
 * hide the difference between "they chose this" and "they went back".
 */
export function startIdentityChange(
  choice: IdentityChoice,
  action = 'identity.choose',
): TenancyIdentity {
  const current = settle(load());
  if (current.identity.in_flight) {
    throw new ApiError('change_under_way', {
      status: 409,
      message: 'a change of identity provider is already under way for this tenant',
    });
  }
  if (choice.kind === 'provider' && !current.providerAvailable) {
    throw new ApiError('provider_identity_unavailable', {
      status: 400,
      message: 'this deployment runs no identity provider of its own',
      fields: { 'identity.kind': 'this deployment runs no identity provider of its own' },
    });
  }

  const started = new Date().toISOString();
  const identity: TenancyIdentity = {
    ...current.identity,
    state: 'applying',
    in_flight: true,
    desired: choice,
    detail: 'asking the provider, then restarting Auth into it',
    started_at: started,
    changed_at: started,
  };
  delete identity.finished_at;
  delete identity.failure;

  save(
    note(
      { ...current, identity },
      {
        slug: STUB_TENANCY.slug,
        actor: STUB_ACCOUNTS.tenancy,
        action,
        outcome: 'success',
        detail: `kind=${choice.kind} issuer=${choice.kind === 'provider' ? STUB_PROVIDER_ISSUER : choice.issuer}`,
      },
    ),
  );
  return readIdentity();
}

/** Tries the declared choice again, without re-typing it. */
export function applyDeclaredIdentity(): TenancyIdentity {
  return startIdentityChange(settle(load()).identity.desired, 'identity.apply');
}

/** Declares what ran before and applies it. */
export function revertIdentity(): TenancyIdentity {
  const current = settle(load());
  const previous = current.identity.previous;
  if (previous === undefined) {
    throw new ApiError('conflict', {
      status: 409,
      message: 'no identity provider was ever applied to this tenant, so there is nothing to go back to',
    });
  }
  return startIdentityChange(previous, 'identity.revert');
}

/**
 * Drops a refused or unfinished attempt.
 *
 * The cluster never moved, so this clears the report and nothing else: the row
 * goes back to what the cluster runs, or to `pending` when it runs nothing.
 */
export function dismissIdentityFailure(): TenancyIdentity {
  const current = settle(load());
  const identity = current.identity;
  if (identity.state !== 'rejected' && identity.state !== 'failed') {
    throw new ApiError('no_failure_to_dismiss', {
      status: 409,
      message: 'the last change of identity provider did not fail, so there is nothing to dismiss',
    });
  }

  const applied = identity.applied;
  const changed = new Date().toISOString();
  const next: TenancyIdentity = {
    ...identity,
    state: applied === undefined ? 'pending' : 'applied',
    in_flight: false,
    desired: applied ?? identity.desired,
    detail: applied === undefined ? 'declared, the cluster has not been told' : 'the attempt was dropped',
    changed_at: changed,
  };
  delete next.failure;

  save(
    note(
      { ...current, identity: next },
      {
        slug: STUB_TENANCY.slug,
        actor: STUB_ACCOUNTS.tenancy,
        action: 'identity.dismiss',
        outcome: 'success',
        detail: `back to ${applied === undefined ? 'nothing applied' : applied.kind}`,
      },
    ),
  );
  return readIdentity();
}

/** Whether the pretend deployment runs an identity provider of its own. */
export function setProviderAvailable(available: boolean): void {
  const current = settle(load());
  save({ ...current, providerAvailable: available });
}

/** A listing. No identity block on any row — the server puts one on cards only. */
export function readTenants(): Tenant[] {
  return settle(load()).tenants.map((tenant) => ({ ...tenant }));
}

/** A card. It always carries the identity block, and it is read-only there. */
export function readTenant(slug: string): Tenant | null {
  const found = settle(load()).tenants.find((tenant) => tenant.slug === slug);
  if (found === undefined) return null;

  // The tenancy the demonstration models has the live block; the others carry
  // a settled one, so that a card is never shown without the field a card has.
  const identity =
    slug === STUB_TENANCY.slug ? readIdentity() : STUB_OTHER_IDENTITIES[slug];
  const report = settle(load()).reports[slug];

  const card: Tenant = { ...found };
  if (identity !== undefined) card.identity = identity;
  // Absent rather than empty for a tenancy nothing has been applied to: the
  // card says so, and there is nothing here for it to say it about.
  if (report !== undefined) card.last_reconcile = report;
  return card;
}

export function writeTenants(tenants: Tenant[]): void {
  save({ ...load(), tenants });
}

/**
 * The certificate the **next** pass over a tenancy reports.
 *
 * Two transitions, and both are the server's rules rather than decoration:
 *
 * - a pair that was **obtained** last time is **kept** this time. While it is
 *   valid and covers the names the authority is not asked at all, and that is
 *   the whole of what keeps a fleet inside limits counted against one registered
 *   domain — a demonstration where pressing the button twice ordered two
 *   certificates would be teaching the opposite;
 * - a pass that **failed** and left a wait behind reports **waiting**: the wait
 *   has not run out, so nothing is asked and the counters stand.
 *
 * A pair somebody else installed, and a tenancy nothing is obtained for, are
 * unchanged by a pass however many times it runs.
 */
function nextCertificate(
  previous: CertificateReport | undefined,
  domain: string,
): CertificateReport {
  if (previous === undefined) return stubFirstCertificate(domain);
  if (previous.outcome === 'obtained') {
    return {
      ...previous,
      outcome: 'kept',
      detail:
        'the pair on the disk is valid and covers every name, so the authority was not asked for anything',
    };
  }
  if (previous.outcome === 'failed') {
    return {
      ...previous,
      outcome: 'waiting',
      detail: 'the wait after the last failure has not run out, so the authority was not asked',
    };
  }
  return previous;
}

/**
 * Where a tenancy stands on what has been rendered for it, after this pass.
 *
 * **Not worked out from the last report**, and that is the point. Whether a role
 * has read a value is decided by whether its process has started since the value
 * was written, and applying a tenancy does not start anything — so a standing is
 * a property of the tenancy that a pass over it leaves alone. Deriving it from
 * the previous report is exactly the mistake the server stopped making: a
 * tenancy proved not to have read a number would turn into a tenancy under it on
 * the next pass, with nothing having restarted.
 *
 * The one pass that does move it is the one that stands a tenancy up: step 10
 * starts its roles for the first time, so they read what was written for them and
 * the standing is `in_force` — there is no "from the first start" about it.
 */
function nextStanding(current: SettingsState | undefined): SettingsState {
  return current ?? 'in_force';
}

/**
 * One application of a tenancy's declared state, and the report it produced.
 *
 * The report is **stored**, because that is what the card shows afterwards, and
 * a demonstration where pressing the button left no trace would be hiding the
 * one block an operator came to this card for.
 *
 * A tenancy whose certificate does not cover a name it publishes under is **not
 * handed over**: the certificate step fails, so the pass is not accepted and
 * neither the state nor the applied generation moves. Reporting the failure and
 * calling the pass accepted anyway is exactly the lie the block exists to
 * prevent.
 */
export function applyStubTenant(slug: string, actor: string): ReconcileRun {
  const current = settle(load());
  const tenant = current.tenants.find((one) => one.slug === slug);
  if (tenant === undefined) throw new ApiError('tenant_not_found', { status: 404 });

  const startedAt = new Date().toISOString();
  const previous = current.reports[slug];
  const certificate = nextCertificate(previous?.certificate, tenant.tenancy_domain);
  // Read from the tenancy rather than from the report it produced last time: the
  // report says where the tenancy stood, and a pass that does not restart a role
  // does not move it.
  const standing = nextStanding(current.standings[slug]);
  const accepted = certificate.missing.length === 0;
  const finishedAt = new Date().toISOString();

  const report: ReconcileReport = {
    tenant: slug,
    generation: tenant.generation,
    actor,
    started_at: startedAt,
    finished_at: finishedAt,
    steps: stubReportSteps(certificate, accepted, standing !== 'not_written'),
    outcome: accepted ? 'accepted' : 'not_accepted',
    transport_ca_fingerprint: tenant.transport_ca_fingerprint,
    certificate,
    anonymous_budget: stubBudgetIn(standing),
    rendered_settings: stubSettingsIn(standing),
  };

  save(
    note(
      {
        ...current,
        tenants: current.tenants.map((one) => {
          if (one.slug !== slug) return one;
          if (!accepted) return { ...one, updated_at: finishedAt };
          return {
            ...one,
            state: 'ready',
            applied_generation: one.generation,
            up_to_date: true,
            updated_at: finishedAt,
          };
        }),
        standings: { ...current.standings, [slug]: standing },
        reports: { ...current.reports, [slug]: report },
      },
      {
        slug,
        actor,
        action: 'tenant.reconcile',
        // The outcome of the pass, not `success`: a pass that ran every step and
        // was not accepted is filed under what it came to.
        outcome: report.outcome,
        detail: `generation=${String(tenant.generation)}`,
      },
    ),
  );

  return { actor, report };
}

export function readResources(): Resource[] {
  return load().resources;
}

// ---------------------------------------------------------------------------
// What a tenancy used
// ---------------------------------------------------------------------------

export function readAuditExport(): boolean {
  return load().auditExport;
}

export function setAuditExport(readable: boolean): void {
  save({ ...load(), auditExport: readable });
}

/**
 * The four figures, assembled the way the server assembles them.
 *
 * Two rules are copied from it deliberately, because they are what the screen
 * has to draw honestly:
 *
 * **A figure is present or named in `unavailable` with a reason.** There is no
 * branch here that answers zero for something it could not count — the reason
 * travels instead of the number, which is the whole difference between "nothing
 * happened" and "nobody could tell".
 *
 * **Connectors and resources are as of now; sessions and people are over the
 * window.** The registry and the catalogue have no history to ask about, so
 * their answers carry the instant they were read and the period is echoed
 * untouched.
 *
 * A tenancy other than the one this demonstration models has no cluster behind
 * it, so all four come back missing. That is the state a tenancy is in between
 * being declared and being handed over, and it is worth being able to see.
 */
export function stubUsage(slug: string, period: UsagePeriod): TenantUsage {
  const current = settle(load());
  const asOf = new Date().toISOString();
  const unavailable: UsageGap[] = [];

  const modelled = slug === STUB_TENANCY.slug;
  const usage: TenantUsage = {
    tenant: slug,
    period,
    connectors: null,
    resources: null,
    sessions: null,
    people: null,
    unavailable,
  };

  if (modelled) {
    usage.connectors = { as_of: asOf, ...STUB_AGENTS };
    usage.resources = { as_of: asOf, ...STUB_RESOURCE_USAGE };
  } else {
    const reason = `the cluster of \`${slug}\` did not answer: no gateway has run for it yet`;
    unavailable.push({ figure: 'connectors', reason }, { figure: 'resources', reason });
  }

  if (modelled && current.auditExport) {
    usage.sessions = { ...STUB_SESSIONS_USAGE };
    usage.people = { ...STUB_PEOPLE_USAGE };
  } else {
    unavailable.push(
      { figure: 'sessions', reason: STUB_EXPORT_UNREADABLE },
      { figure: 'people', reason: STUB_EXPORT_UNREADABLE },
    );
  }

  return usage;
}

// ---------------------------------------------------------------------------
// Versions, and rolling one out
// ---------------------------------------------------------------------------

/** How long a wave takes to move here. Long enough to see it moving. */
const MOVE_MS = 6000;

/** The soak the server asks for by default, in seconds. Shown, never waited out. */
const SOAK_SECS = 30 * 60;

const WAVES: RolloutWave[] = ['canary', 'rest'];

/** The states a reconcile may run against, and therefore the ones a wave moves. */
const MOVABLE = ['requested', 'provisioning', 'ready', 'failed'];

function waveOf(current: StubState, slug: string): string {
  return current.waves[slug] ?? 'rest';
}

/**
 * What a tenancy's components were seen running.
 *
 * A tenancy with no readings at all gets the four with their reasons — the two
 * that can be asked saying they have not been asked yet, and the two that cannot
 * be asked saying why they never can. There is no arm here that fills a version
 * in from the tenancy's declared tag.
 */
function versionsOf(current: StubState, slug: string): ComponentVersion[] {
  return (
    current.versions[slug] ?? [
      { component: 'client', reason: STUB_CLIENT_UNTOLD },
      stubUnread('proxy', STUB_NOT_READ),
      stubUnread('authority', STUB_NOT_READ),
      { component: 'connector', reason: STUB_CONNECTOR_UNTOLD },
    ]
  );
}

function movable(tenant: Tenant): boolean {
  return MOVABLE.includes(tenant.state);
}

/** Whether its last reconcile was accepted — the bar a tenancy is handed over at. */
function acceptedOf(tenant: Tenant): boolean {
  return tenant.applied_generation !== null && tenant.state !== 'failed';
}

function stepOf(current: StubState, slug: string): StubStep | undefined {
  return current.steps.find((step) => step.slug === slug);
}

/**
 * Whether a rollout in this state is still going.
 *
 * The server's own division, and the one the console reads off `live`:
 * `applying` and `waiting` are going; `halted` and `done` are two ways of
 * having ended, and neither of them holds anything up.
 */
function live(state: string): boolean {
  return state === 'applying' || state === 'waiting';
}

/**
 * A wave that has been moving long enough finishes moving, whoever reads next.
 *
 * By the clock rather than on a timer, like the change of identity provider: it
 * then also finishes across the reload between the two entry points, and nothing
 * depends on a browser tab staying open.
 */
function settleRollout(current: StubState): StubState {
  const rollout = current.rollout;
  if (rollout === null || rollout.state !== 'applying') return current;

  const since = rollout.movingSince;
  if (since !== null && Date.now() - new Date(since).getTime() < MOVE_MS) return current;

  return moveWave(current, rollout.wave, rollout.target, rollout.moving);
}

/**
 * Moves the tenancies of one wave, in name order, and leaves the wave waiting.
 *
 * One at a time and in a fixed order, as the server does it: moving a wave at
 * once would multiply one outage by the size of the wave, and a half-finished
 * wave has to be readable afterwards.
 *
 * `movers` is `null` for the whole wave and a list of slugs for a second
 * attempt. That is the only difference between starting a wave and trying one
 * again — and a step that already exists is **counted up** rather than
 * rewritten, exactly as the server's storage does it, so a tenancy that failed
 * and was then moved goes on saying that it failed once.
 */
function moveWave(
  current: StubState,
  wave: string,
  target: string,
  movers: string[] | null,
): StubState {
  const at = new Date().toISOString();
  const moving = current.tenants
    .filter((tenant) => movable(tenant) && waveOf(current, tenant.slug) === wave)
    .map((tenant) => tenant.slug)
    .filter((slug) => movers === null || movers.includes(slug))
    .sort((a, b) => a.localeCompare(b));

  const steps = current.steps.map((step) => ({ ...step }));
  const versions = { ...current.versions };
  const observedAt = { ...current.observedAt };

  const tenants = current.tenants.map((tenant) => {
    if (!moving.includes(tenant.slug)) return tenant;

    const from = tenant.version;
    // The version is written first and the tenancy reconciled second, which is
    // the server's order: a reconcile that ran before the row was written would
    // bring the cluster up on exactly what it was already on.
    //
    // The one that fails goes on failing until somebody has put it right, which
    // is the whole reason trying again exists.
    const moved = tenant.slug !== STUB_MOVE_FAILS || current.repaired;
    const before = steps.find((step) => step.slug === tenant.slug);
    if (before === undefined) {
      steps.push({
        slug: tenant.slug,
        wave,
        outcome: moved ? 'moved' : 'failed',
        from,
        detail: moved ? 'accepted' : STUB_MOVE_FAILURE,
        attempts: 1,
        failures: moved ? 0 : 1,
        changed_at: at,
      });
    } else {
      // `from` is not rewritten: by now the tenancy is already carrying the
      // target, so what it came from lives only here.
      before.wave = wave;
      before.outcome = moved ? 'moved' : 'failed';
      before.detail = moved ? 'accepted' : STUB_MOVE_FAILURE;
      before.attempts += 1;
      before.failures += moved ? 0 : 1;
      before.changed_at = at;
    }

    if (moved) {
      // An accepted reconcile restarts the containers and reads them, so the
      // readings become the target. A move that was not accepted leaves the
      // readings as they were: nothing was learned.
      versions[tenant.slug] = [
        { component: 'client', reason: STUB_CLIENT_UNTOLD },
        stubRead('proxy', target),
        stubRead('authority', target),
        { component: 'connector', reason: STUB_CONNECTOR_UNTOLD },
      ];
      observedAt[tenant.slug] = at;
      return {
        ...tenant,
        version: target,
        state: 'ready',
        applied_generation: tenant.generation,
        up_to_date: true,
        updated_at: at,
      };
    }
    return { ...tenant, version: target, up_to_date: false, updated_at: at };
  });

  const rollout = current.rollout;
  // One line per tenancy the wave touched, as the server files them: the
  // outcome is the move's own, so a failure is legible in the trail and not
  // only on the board.
  const trail = moving.reduce<StubState['trail']>((so_far, slug) => {
    const step = steps.find((one) => one.slug === slug);
    if (step === undefined) return so_far;
    return note({ ...current, trail: so_far }, {
      slug,
      actor: rollout?.actor ?? STUB_ACCOUNTS.admin,
      action: 'rollout.move',
      outcome: step.outcome,
      detail: `wave=${wave} from=${step.from} ${step.detail}`,
    }).trail;
  }, current.trail);

  return save({
    ...current,
    tenants,
    steps,
    versions,
    observedAt,
    trail,
    rollout:
      rollout === null
        ? null
        : {
            ...rollout,
            state: 'waiting',
            detail: `the ${wave} wave has been dealt with and is waiting to be signed off`,
            changed_at: at,
            movingSince: null,
            moving: null,
          },
  });
}

/**
 * Everything this control plane can see going wrong with the wave under way.
 *
 * The same four kinds the server names, in its own words, because the sentence
 * that comes out of them is what somebody reads on the board and in the refusal:
 * a tenancy that was not moved, a move that failed, one whose own diagnosis does
 * not accept it, and a pair of its components with a named break between them.
 */
function findings(current: StubState, wave: string): string[] {
  const found: string[] = [];
  for (const tenant of current.tenants) {
    if (!movable(tenant) || waveOf(current, tenant.slug) !== wave) continue;

    const step = stepOf(current, tenant.slug);
    if (step === undefined || step.outcome === 'pending') {
      found.push(`${tenant.slug} has not been moved to the target version`);
    } else if (step.outcome === 'failed') {
      found.push(`${tenant.slug} could not be moved: ${step.detail}`);
    } else if (!acceptedOf(tenant)) {
      found.push(
        `${tenant.slug} runs the target version and its own diagnosis does not accept it`,
      );
    }

    for (const pair of judgePairs(versionsOf(current, tenant.slug))) {
      if (pair.verdict === 'broken') {
        found.push(`${tenant.slug}: ${pair.between.join('↔')}: ${pair.detail ?? ''}`);
      }
    }
  }
  return found;
}

/** Whether the wave under way may be signed off, in the server's own words. */
function advanceOf(current: StubState): { may: boolean; blocked?: string; next?: string } {
  const rollout = current.rollout;
  if (rollout === null) return { may: false };

  if (rollout.state === 'applying') {
    return {
      may: false,
      blocked:
        'this wave is still being moved; it can be signed off when every tenant in it has been ' +
        'dealt with',
    };
  }
  if (rollout.state === 'halted') {
    return {
      may: false,
      blocked:
        'this rollout was stopped; what moved stays where it is, and going on means starting ' +
        'another',
    };
  }
  if (rollout.state === 'done') {
    return { may: false, blocked: 'this rollout is over; there is no wave to sign off' };
  }

  const found = findings(current, rollout.wave);
  if (found.length > 0) {
    return {
      may: false,
      blocked: `the wave under way is not sound, so the next one does not start: ${found.join('; ')}`,
    };
  }
  if (!current.soaked) {
    const stood = Math.floor((Date.now() - new Date(rollout.changed_at).getTime()) / 1000);
    const left = Math.max(1, SOAK_SECS - stood);
    return {
      may: false,
      blocked:
        'this wave has been at the target version for less than the soak this deployment asks ' +
        `for; ${String(left)} seconds to go. A canary signed off the moment it finished moving is ` +
        'a canary that was never given anything to sing about',
    };
  }

  const after = WAVES[WAVES.indexOf(rollout.wave as RolloutWave) + 1];
  return after === undefined ? { may: true } : { may: true, next: after };
}

/**
 * Which tenancies of the wave under way a second attempt would move, in the
 * server's own words when it would move none.
 *
 * The way out of a wave one tenancy failed in, and not a way past it: the
 * tenancies here are the ones that did **not** move, the wave still has to be
 * signed off afterwards, and the wave after it still waits for that.
 */
function retryOf(current: StubState): { may: boolean; blocked?: string; tenants: string[] } {
  const rollout = current.rollout;
  if (rollout === null) return { may: false, tenants: [] };

  if (rollout.state === 'applying') {
    return {
      may: false,
      tenants: [],
      blocked:
        'this wave is still being moved; what did not work can be tried again once every tenant ' +
        'in it has been dealt with',
    };
  }
  if (rollout.state === 'halted') {
    return {
      may: false,
      tenants: [],
      blocked:
        'this rollout was stopped; what moved stays where it is, and going on means starting ' +
        'another',
    };
  }
  if (rollout.state === 'done') {
    return { may: false, tenants: [], blocked: 'this rollout is over; there is no wave to move again' };
  }

  const again = current.tenants
    .filter(
      (tenant) =>
        movable(tenant) &&
        waveOf(current, tenant.slug) === rollout.wave &&
        stepOf(current, tenant.slug)?.outcome !== 'moved',
    )
    .map((tenant) => tenant.slug)
    .sort((a, b) => a.localeCompare(b));

  if (again.length === 0) {
    return {
      may: false,
      tenants: [],
      blocked:
        'every tenant of this wave was moved, so there is no move to run again; anything still ' +
        'holding the wave is about the tenant itself and is put right on the tenant’s own card',
    };
  }
  return { may: true, tenants: again };
}

/**
 * The rollout on record, going or over.
 *
 * A rollout that ended stays here, as it does on the server: it is what says
 * which tenancies were moved before it ended and which were not, and `live` is
 * what tells a board reader whether anything is happening. Leaving it out would
 * put the console back to deciding by the presence of the block.
 */
function rolloutBody(current: StubState): Rollout | undefined {
  const rollout = current.rollout;
  if (rollout === null) return undefined;

  const advance = advanceOf(current);
  const again = retryOf(current);
  const seen = current.tenants
    .filter((tenant) => movable(tenant))
    .map((tenant) => versionsOf(current, tenant.slug));
  const ahead = forecast(rollout.target, seen);

  const body: Rollout = {
    target: rollout.target,
    state: rollout.state,
    live: live(rollout.state),
    wave: rollout.wave,
    detail: rollout.detail,
    actor: rollout.actor,
    started_at: rollout.started_at,
    changed_at: rollout.changed_at,
    advance: {
      may: advance.may,
      ...(advance.blocked === undefined ? {} : { blocked_by: advance.blocked }),
      ...(advance.next === undefined ? {} : { next: advance.next }),
    },
    retry: {
      may: again.may,
      ...(again.blocked === undefined ? {} : { blocked_by: again.blocked }),
      // Left out when empty, as the server leaves it out.
      ...(again.tenants.length === 0 ? {} : { tenants: again.tenants }),
    },
    crosses: ahead.crosses,
  };
  if (rollout.finished_at !== null) body.finished_at = rollout.finished_at;
  if (ahead.unavailable !== undefined) body.forecast_unavailable = ahead.unavailable;
  return body;
}

function rowOf(current: StubState, tenant: Tenant): TenantVersions {
  const components = versionsOf(current, tenant.slug);
  const step = stepOf(current, tenant.slug);
  const observed = current.observedAt[tenant.slug];

  const row: TenantVersions = {
    slug: tenant.slug,
    state: tenant.state,
    wave: waveOf(current, tenant.slug),
    movable: movable(tenant),
    declared: tenant.version,
    accepted: acceptedOf(tenant),
    components: components.map((component) => ({ ...component })),
    pairs: judgePairs(components),
  };
  if (observed !== undefined) row.observed_at = observed;
  if (step !== undefined) {
    const move: TenantMove = {
      outcome: step.outcome,
      from: step.from,
      detail: step.detail,
      attempts: step.attempts,
      failures: step.failures,
      changed_at: step.changed_at,
    };
    row.step = move;
  }
  return row;
}

/** The board, assembled the way the server assembles it. */
export function stubBoard(): RolloutBoard {
  const current = settleRollout(settle(load()));
  const rows = current.tenants.map((tenant) => rowOf(current, tenant));

  const board: RolloutBoard = {
    waves: WAVES.map((wave, position) => ({
      wave,
      position,
      tenants: rows.filter((row) => row.wave === wave && row.movable).length,
      // The wave a rollout is **on**, which a rollout that has ended is not on
      // any more: the board goes on showing the last one, and a wave still
      // marked as moving under it would say something is happening.
      under_way: current.rollout !== null && live(current.rollout.state) && current.rollout.wave === wave,
    })),
    tenants: rows,
  };
  const rollout = rolloutBody(current);
  if (rollout !== undefined) board.rollout = rollout;
  return board;
}

/**
 * What the server says to both of the things a rollout under way holds up: a
 * second rollout, and a change of the wave a tenancy moves in.
 *
 * One sentence for both because the server has one error for both — and it
 * names the way through, which is the same way through either way.
 */
const ROLLOUT_UNDER_WAY =
  'a rollout is under way; it moves the fleet a wave at a time, so neither a second one nor a ' +
  'change of the wave a tenant moves in is taken while it runs — stop the rollout, make the ' +
  'change, and start another';

/**
 * Puts one tenancy in a wave. A tenancy that is not there answers as absent.
 *
 * Refused while a rollout is going, as the server refuses it: a wave's
 * tenancies are read when the wave starts moving, so a tenancy that changes
 * wave halfway through is one the rollout has stopped counting — its own
 * trouble leaves the wave under way with it, and the wave it joins may already
 * have run.
 */
export function setStubWave(
  slug: string,
  wave: RolloutWave,
  actor: string = STUB_ACCOUNTS.admin,
): RolloutBoard {
  const current = settleRollout(settle(load()));
  if (!current.tenants.some((tenant) => tenant.slug === slug)) {
    throw new ApiError('tenant_not_found', { status: 404 });
  }
  if (current.rollout !== null && live(current.rollout.state)) {
    throw new ApiError('conflict', { status: 409, message: ROLLOUT_UNDER_WAY });
  }
  save(
    note(
      { ...current, waves: { ...current.waves, [slug]: wave } },
      { slug, actor, action: 'rollout.wave', outcome: 'success', detail: `wave=${wave}` },
    ),
  );
  return stubBoard();
}

/**
 * Starts a rollout, and refuses the two things the server refuses.
 *
 * A second **going** rollout, because at most one is live; and a rollout whose
 * first wave holds nobody, because that would move the whole fleet on the
 * strength of a wave that showed nothing.
 *
 * A rollout that ended — stopped, or signed off to the last wave — is not in
 * the way. It stays on the board, and refusing the next one on the strength of
 * it would leave an operator with the fleet where the trouble is and no way out.
 */
export function startStubRollout(target: string, actor: string): RolloutBoard {
  const current = settleRollout(settle(load()));
  if (current.rollout !== null && live(current.rollout.state)) {
    throw new ApiError('conflict', { status: 409, message: ROLLOUT_UNDER_WAY });
  }

  const first = WAVES[0] ?? 'canary';
  const canary = current.tenants.filter(
    (tenant) => movable(tenant) && waveOf(current, tenant.slug) === first,
  );
  if (canary.length === 0) {
    throw new ApiError('conflict', {
      status: 409,
      message:
        'the canary wave holds no tenant a reconcile may run against, so this would be a rollout ' +
        'rather than a canary rollout',
    });
  }

  const at = new Date().toISOString();
  save({
    ...current,
    steps: [],
    rollout: {
      target,
      state: 'applying',
      wave: first,
      detail: `moving the ${first} wave`,
      actor,
      started_at: at,
      changed_at: at,
      finished_at: null,
      movingSince: at,
      moving: null,
    },
  });
  return stubBoard();
}

/** Signs the wave under way off and starts the next, or finishes the rollout. */
export function advanceStubRollout(): RolloutBoard {
  const current = settleRollout(settle(load()));
  const rollout = current.rollout;
  if (rollout === null) {
    throw new ApiError('conflict', { status: 409, message: 'no rollout is under way' });
  }

  // A rollout that ended is refused by the same function and with the same
  // sentence the board shows: `advanceOf` looks at the state before it looks at
  // a tenancy.
  const advance = advanceOf(current);
  if (!advance.may) {
    throw new ApiError('conflict', {
      status: 409,
      message: advance.blocked ?? 'this wave may not be signed off',
    });
  }

  const at = new Date().toISOString();
  const next = advance.next;
  save({
    ...current,
    rollout:
      next === undefined
        ? {
            ...rollout,
            state: 'done',
            detail: 'every wave was signed off',
            changed_at: at,
            finished_at: at,
            movingSince: null,
            moving: null,
          }
        : {
            ...rollout,
            state: 'applying',
            wave: next,
            detail: `moving the ${next} wave`,
            changed_at: at,
            movingSince: at,
            moving: null,
          },
  });
  return stubBoard();
}

/**
 * Moves the tenancies of the wave under way that did not move.
 *
 * Only they move; the wave still has to be signed off, and the next one still
 * waits for that sign-off. Which tenancies these are is worked out from the
 * steps rather than taken from the caller — there is no argument here, as there
 * is no body on the route.
 */
export function retryStubRollout(): RolloutBoard {
  const current = settleRollout(settle(load()));
  const rollout = current.rollout;
  if (rollout === null) {
    throw new ApiError('conflict', { status: 409, message: 'no rollout is under way' });
  }

  const again = retryOf(current);
  if (!again.may) {
    throw new ApiError('conflict', {
      status: 409,
      message: again.blocked ?? 'there is no move to run again',
    });
  }

  const at = new Date().toISOString();
  save({
    ...current,
    rollout: {
      ...rollout,
      state: 'applying',
      detail: `moving the tenants of the ${rollout.wave} wave that did not move`,
      changed_at: at,
      movingSince: at,
      moving: again.tenants,
    },
  });
  return stubBoard();
}

/**
 * Stops it. What has moved stays moved — there is no undo here either.
 *
 * Stopping **ends** the rollout: it stays on the board as the last one, with
 * the steps that say who was moved, and it holds nothing up afterwards. An
 * ended rollout has nothing left to stop, and that answers `409`.
 */
export function stopStubRollout(): RolloutBoard {
  const current = settleRollout(settle(load()));
  const rollout = current.rollout;
  if (rollout === null || !live(rollout.state)) {
    throw new ApiError('conflict', { status: 409, message: 'no rollout is under way' });
  }

  const at = new Date().toISOString();
  save({
    ...current,
    rollout: {
      ...rollout,
      state: 'halted',
      detail: 'stopped by an operator',
      changed_at: at,
      finished_at: at,
      movingSince: null,
      moving: null,
    },
  });
  return stubBoard();
}

export function readStubSoaked(): boolean {
  return load().soaked;
}

export function setStubSoaked(soaked: boolean): void {
  save({ ...load(), soaked });
}

/**
 * Whether the tenancy whose move fails has been put right.
 *
 * The half of trying again that happens away from this console. With it off, a
 * second attempt fails again and the step counts another failure; with it on,
 * the tenancy moves and its step reads `moved` with the failure still behind
 * it — which is the fact the board has to keep.
 */
export function readStubRepaired(): boolean {
  return load().repaired;
}

export function setStubRepaired(repaired: boolean): void {
  save({ ...load(), repaired });
}

// ---------------------------------------------------------------------------
// The way in, played out rather than assumed
// ---------------------------------------------------------------------------

/** What the pretend provider will answer, and the switch that sets it. */
export function readVouch(): StubVouch {
  return load().vouch;
}

export function setVouch(vouch: StubVouch): void {
  save({ ...load(), vouch });
}

/**
 * Starts a sign-in the way the server does: the `state` is written down here
 * and not handed to the caller as anything it controls. The pretend provider
 * gives it back in the address, and `completeStubSignIn` compares the two.
 */
export function startStubSignIn(): { state: string } {
  const current = settle(load());
  const value = `stub-${Math.random().toString(36).slice(2)}`;
  save({ ...current, signInState: value });
  return { state: value };
}

/**
 * Finishes it, once. The row is taken and dropped whatever happens next, so
 * the same code and the same `state` cannot be finished twice — which is what
 * makes a reload of a return read as expired here as it does in a deployment.
 */
export function completeStubSignIn(state: string): ClusterSession {
  const current = settle(load());
  const expected = current.signInState;
  save({ ...current, signInState: null });

  if (expected === null || state !== expected) {
    throw new ApiError('sign_in_expired', {
      status: 400,
      message: 'this sign-in was started in another browser, or too long ago',
    });
  }
  if (current.identity.applied === undefined || current.identity.in_flight) {
    throw new ApiError('sign_in_unavailable', {
      status: 409,
      message: 'this cluster runs no identity provider at the moment',
    });
  }

  switch (current.vouch) {
    case 'refused':
      throw new ApiError('sign_in_refused', {
        status: 401,
        message: 'the identity did not pass verification',
      });
    case 'unreachable':
      throw new ApiError('provider_unreachable', {
        status: 502,
        message: 'the identity provider did not answer the token request',
      });
    case 'stranger':
      throw new ApiError('forbidden', {
        status: 403,
        message: 'this tenancy does not name you an administrator of its cluster',
      });
    case 'admin':
      break;
  }

  save({ ...load(), clusterSignedIn: true });
  return stubClusterSession();
}

/** The session such a sign-in produced, as long as one is open. */
export function readClusterSession(): ClusterSession | null {
  const current = settle(load());
  if (!current.clusterSignedIn || current.identity.applied === undefined) return null;
  return stubClusterSession();
}

export function clearClusterSession(): void {
  save({ ...load(), clusterSignedIn: false, signInState: null });
}

function stubClusterActor(): ClusterActor {
  const current = load();
  return {
    subject: current.nameable ? STUB_CLUSTER_ACTOR.subject : STUB_UNNAMEABLE_SUBJECT,
    display_name: STUB_CLUSTER_ACTOR.display_name,
    groups: [...STUB_CLUSTER_ACTOR.groups],
  };
}

function stubClusterSession(): ClusterSession {
  return {
    actor: stubClusterActor(),
    role: 'cluster_admin',
    tenancy: STUB_TENANCY,
    // A stub tenancy runs a current build, so it is offered everything. The
    // field is spelled out rather than left off because leaving it off is a
    // different case — an older control plane — and the console behaves
    // differently in the two.
    resource_kinds: [...RESOURCE_KINDS],
  };
}

// ---------------------------------------------------------------------------
// Writing the catalogue, and the two ways it does not go through
// ---------------------------------------------------------------------------

export function readClusterReachable(): boolean {
  return load().clusterReachable;
}

export function setClusterReachable(reachable: boolean): void {
  save({ ...load(), clusterReachable: reachable });
}

export function readNameable(): boolean {
  return load().nameable;
}

export function setNameable(nameable: boolean): void {
  save({ ...load(), nameable });
}

/**
 * A name gdsgate will not record stops the write here, with nothing declared
 * and nothing carried — which is the server's own order of events.
 */
function requireNameable(): void {
  if (load().nameable) return;
  throw new ApiError('operator_not_nameable', {
    status: 409,
    message: 'the caller cannot be named to the cluster, so nothing was changed',
  });
}

/**
 * Declared first, carried second. An unreachable cluster leaves the
 * declaration standing — so the resource **is** in this list afterwards, and
 * the refusal is about the cluster rather than about the write.
 */
export function writeStubResource(id: string, input: ResourceInput): void {
  requireNameable();

  const current = settle(load());
  const written: Resource = { id, ...input };
  const at = current.resources.findIndex((resource) => resource.id === id);
  const resources =
    at === -1
      ? [...current.resources, written]
      : current.resources.map((resource, index) => (index === at ? written : resource));

  save({ ...current, resources });
  requireReachable();
}

/** The boundaries the stub tenancy declares, with the rows a cluster holds. */
export function stubScopes(): Scope[] {
  return settle(load()).scopes;
}

export function writeStubScope(prefix: string, input: Scope): void {
  requireNameable();

  const current = settle(load());
  const written: Scope = { ...input, prefix };
  const at = current.scopes.findIndex((scope) => scope.prefix === prefix);
  const scopes =
    at === -1
      ? [...current.scopes, written]
      : current.scopes.map((scope, index) => (index === at ? written : scope));

  save({ ...current, scopes });
  requireReachable();
}

export function removeStubScope(prefix: string): void {
  requireNameable();

  const current = settle(load());
  if (!current.scopes.some((scope) => scope.prefix === prefix)) {
    throw new ApiError('resource_not_found', {
      status: 404,
      message: `no boundary \`${prefix}\` is declared for this tenant`,
    });
  }

  save({ ...current, scopes: current.scopes.filter((scope) => scope.prefix !== prefix) });
  requireReachable();
}

export function removeStubResource(id: string): void {
  requireNameable();

  const current = settle(load());
  if (!current.resources.some((resource) => resource.id === id)) {
    throw new ApiError('resource_not_found', {
      status: 404,
      message: `no resource \`${id}\` is declared for this tenant`,
    });
  }

  save({ ...current, resources: current.resources.filter((resource) => resource.id !== id) });
  requireReachable();
}

function requireReachable(): void {
  if (load().clusterReachable) return;
  throw new ApiError('tenant_unreachable', {
    status: 502,
    message:
      'the declaration stands; the cluster was not reached, and the next application will not carry your name',
  });
}
